import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminAiUsage({ adminToken }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/ai-usage`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(r => setData(r.data)).catch(() => toast.error("KI-Nutzung konnte nicht geladen werden"));
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body, msg) => {
    await axios.post(`${API}/admin/ai-usage/${path}`, body, { headers: { Authorization: `Bearer ${adminToken}` } });
    toast.success(msg);
    load();
  };

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;

  const { breaker, days, top_ips, limits } = data;
  const max = Math.max(1, ...days.map(d => d.allowed + d.blocked));

  return (
    <div className="space-y-6" data-testid="ai-usage-panel">
      <div className={`rounded-xl border p-5 flex flex-wrap items-center justify-between gap-4 ${breaker.open ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-center gap-3">
          {breaker.open ? <ShieldAlert className="w-6 h-6 text-red-600" /> : <ShieldCheck className="w-6 h-6 text-emerald-600" />}
          <div>
            <p className="font-semibold text-slate-900" data-testid="breaker-status">
              {breaker.manual_block ? "Manuell gesperrt" : breaker.auto_tripped ? "Tageslimit erreicht – automatisch gesperrt" : "Generierung aktiv"}
            </p>
            <p className="text-sm text-slate-600" data-testid="breaker-usage">Heute: {breaker.used_today} / {breaker.daily_limit} KI-Aufrufe (ohne Zahlung)</p>
          </div>
        </div>
        <Button data-testid="breaker-toggle-btn" size="sm" variant="outline"
          onClick={() => post("breaker", { blocked: !breaker.manual_block }, breaker.manual_block ? "KI-Generierung freigegeben" : "KI-Generierung gesperrt")}>
          {breaker.manual_block ? "Manuelle Sperre aufheben" : "Generierung sperren"}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">Letzte 7 Tage</h3>
        <div className="space-y-2">
          {days.map(d => (
            <div key={d.date} className="flex items-center gap-3 text-sm" data-testid={`usage-day-${d.date}`}>
              <span className="w-24 text-slate-500">{d.date}</span>
              <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden flex">
                <div className="bg-indigo-500" style={{ width: `${(d.allowed / max) * 100}%` }} />
                <div className="bg-red-400" style={{ width: `${(d.blocked / max) * 100}%` }} />
              </div>
              <span className="w-36 text-right text-slate-700">{d.allowed} ok · {d.blocked} blockiert</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-1">Top-IPs heute</h3>
        <p className="text-xs text-slate-500 mb-4">
          Limits pro IP: {limits.generate_hourly}/Std und {limits.generate_daily}/Tag Generierungen, {limits.check_hourly}/Std Website-Checks.
        </p>
        {top_ips.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="no-top-ips">Heute noch keine Aufrufe.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500"><th className="py-1">IP</th><th>Erlaubt</th><th>Blockiert</th><th /></tr></thead>
            <tbody>
              {top_ips.map(t => (
                <tr key={t.ip} className="border-t border-slate-100" data-testid={`top-ip-row-${t.ip}`}>
                  <td className="py-2 font-mono">{t.ip}</td>
                  <td>{t.allowed}</td>
                  <td>{t.blocked}</td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" data-testid={`ip-block-btn-${t.ip}`}
                      onClick={() => post("block-ip", { ip: t.ip, blocked: !t.ip_blocked }, t.ip_blocked ? `${t.ip} entsperrt` : `${t.ip} gesperrt`)}>
                      {t.ip_blocked ? "Entsperren" : "Sperren"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminAiUsage;
